/*
 * AAIKO cart mutations, shared by the bag panel (aaiko-cart-drawer.js) and the
 * /cart page (aaiko-cart-page.js), so both change a line the same way.
 *
 * Shopify stays the cart engine. Everything here is a POST to its Ajax API
 * with the Section Rendering API's `sections` parameter, so the caller gets
 * its own fresh markup back in the same response. Nothing about cart state is
 * computed here.
 *
 * Loaded by both surfaces, possibly twice on /cart: it defines itself once.
 */
(function () {
  'use strict';

  if (window.AaikoCart) return;

  var CART_EVENT = 'shopify:cart:lines-update';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function sectionsUrl() {
    return window.location.pathname + window.location.search;
  }

  /**
   * POST JSON to the Ajax API. A non-2xx answer rejects with Shopify's own
   * description (e.g. "You can only add 2 of this item to your cart"), which
   * is already in the shopper's language.
   * @param {string} path
   * @param {object} body
   * @returns {Promise<object>}
   */
  function post(path, body) {
    return fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!response.ok || (data && data.status && Number(data.status) >= 400)) {
            var error = new Error((data && (data.description || data.message)) || response.statusText);
            error.status = response.status;
            error.userMessage = data && typeof data.description === 'string' ? data.description : '';
            throw error;
          }
          return data;
        });
    });
  }

  /**
   * Set a line's quantity (0 removes it).
   * @param {number} line 1-based line position
   * @param {number} quantity
   * @param {string} sections comma-separated section ids to render back
   */
  function change(line, quantity, sections) {
    return post('cart/change.js', {
      line: line,
      quantity: quantity,
      sections: sections,
      sections_url: sectionsUrl(),
    });
  }

  /**
   * Replace a line's variant, which is how a size changes. The Ajax API has no
   * call for that, so: add the new variant with the line's quantity and
   * properties, then set the old line (by key, since the add shifts line
   * positions) to 0. Add first, so a refused add (sold out, not enough stock)
   * leaves the bag exactly as it was.
   *
   * @param {{key: string, variantId: number, quantity: number, properties?: object, sellingPlan?: number, sections: string}} options
   */
  function swapVariant(options) {
    var item = { id: Number(options.variantId), quantity: Number(options.quantity) || 1 };
    if (options.properties && Object.keys(options.properties).length) item.properties = options.properties;
    if (options.sellingPlan) item.selling_plan = Number(options.sellingPlan);
    return post('cart/add.js', { items: [item] }).then(function () {
      return post('cart/change.js', {
        id: options.key,
        quantity: 0,
        sections: options.sections,
        sections_url: sectionsUrl(),
      });
    });
  }

  /**
   * Tell the rest of the page the bag changed: the header count, the other
   * cart surface, Horizon's own listeners. `source` lets the sender ignore its
   * own echo. Action `update`, so the bag panel refreshes without opening.
   * @param {string} source
   * @param {object} data the cart JSON from the mutation
   */
  function announce(source, data) {
    var count = data && typeof data.item_count === 'number' ? data.item_count : null;
    var event = new CustomEvent(CART_EVENT, {
      bubbles: true,
      detail: { source: source, itemCount: count },
    });
    event.action = 'update';
    event.promise = Promise.resolve({ cart: { totalQuantity: count }, detail: { itemCount: count } });
    document.dispatchEvent(event);
  }

  /**
   * Reads the optional line properties a size select carries.
   * @param {HTMLElement} select
   * @returns {object|undefined}
   */
  function propertiesOf(select) {
    if (!select.dataset.properties) return undefined;
    try {
      return JSON.parse(select.dataset.properties);
    } catch (error) {
      return undefined;
    }
  }

  window.AaikoCart = {
    EVENT: CART_EVENT,
    change: change,
    swapVariant: swapVariant,
    announce: announce,
    propertiesOf: propertiesOf,
  };
})();
